/**
 * Staged creator-sheet import review — client-side step machine for the
 * upload -> mapping -> resolve-sheet-issues -> resolve-roster-conflicts ->
 * confirm -> importing -> done flow (spec §4). Deliberately plain
 * useState/useCallback, not React Query — each step is a one-shot imperative
 * server round-trip that replaces the in-memory row snapshot, not a
 * cacheable/re-fetchable resource.
 */
import { useCallback, useState } from 'react';
import { ApiError } from '@discovr/common';
import type { ExpectedField, ImportBatch, ImportBatchRow, SheetColumnPreview } from '@discovr/types';
import type { ImportBatchApi } from '../api/importBatchMutations';
import { useCampaignCapabilitiesStore } from '../state/useCampaignCapabilitiesStore';
import { hadSheetIssues, conflictRows } from './importWizardSkipRules';

// Structured `details` payloads the backend attaches to a 409 CONFLICT (see
// discovr-backend's ActiveImportConflictDetails / RosterDriftConflictDetails)
// — narrowed from `unknown` only where actually read, never trusted blindly.
function conflictDetails<T>(err: unknown): T | undefined {
  if (!(err instanceof ApiError) || err.code !== 'CONFLICT') return undefined;
  return err.details as T | undefined;
}

export type ImportWizardStep =
  | 'upload'
  | 'mapping'
  | 'resolve-sheet-issues'
  | 'resolve-roster-conflicts'
  | 'confirm'
  | 'importing'
  | 'done';

/** Which client step a batch's server-side status resumes into. `null` for a
 *  status this client-side flow can't drop back into mid-way (a commit
 *  that's already finished/failed/been discarded) — those fall back to the
 *  plain "already in progress, discard?" card instead of an offered resume. */
function stepForBatchStatus(status: ImportBatch['status']): ImportWizardStep | null {
  switch (status) {
    case 'mapping':
      return 'mapping';
    case 'sheet_review':
      return 'resolve-sheet-issues';
    case 'roster_review':
      return 'resolve-roster-conflicts';
    default:
      return null;
  }
}

export interface ImportWizardState {
  step: ImportWizardStep;
  importId: string | null;
  fileName: string | null;
  sheetColumns: SheetColumnPreview[];
  expectedFields: ExpectedField[];
  /** Data-row count from the last preview response — footer text on the
   *  mapping step ("N data rows · M columns mapped"). 0 before any upload. */
  rowCount: number;
  /** True while checkStatus's initial "is an import already active" /
   *  "what are this campaign's expected columns" check is in flight, right
   *  after the wizard opens and before any file is chosen. */
  checkingActive: boolean;
  rows: ImportBatchRow[];
  /** Row numbers deflected to "keep existing" by a roster-drift re-check at
   *  commit time (spec §5 edge case 5). Populated best-effort by markDone
   *  once the commit finishes — empty until then, and stays empty if the
   *  fetch fails (never blocks the wizard from showing the commit result
   *  itself). */
  deflectedRowNumbers: number[];
  busy: boolean;
  error: string | null;
  /** Set when a preview upload 409s because the campaign already has an
   *  active batch (someone else uploading, another tab, or an orphaned
   *  batch from a crashed/closed session) — the id of the batch actually
   *  blocking this one, so the UI can offer discarding it instead of a bare
   *  error with no recovery path. Cleared once the admin discards it or
   *  starts a fresh upload. */
  activeConflictImportId: string | null;
  /** The full batch behind `activeConflictImportId`, fetched best-effort
   *  whenever that id is set — non-null only when its status maps to a
   *  resumable step (see `stepForBatchStatus`), so the UI can offer "Resume"
   *  instead of only "Discard". This is what makes a same-browser page
   *  refresh mid-review recoverable: the server already persisted every
   *  resolution, this just rehydrates the client's local step machine from
   *  it instead of always treating an active batch as a pure conflict. */
  resumableBatch: ImportBatch | null;
}

const initialState: ImportWizardState = {
  step: 'upload',
  importId: null,
  fileName: null,
  sheetColumns: [],
  expectedFields: [],
  rowCount: 0,
  checkingActive: false,
  rows: [],
  deflectedRowNumbers: [],
  busy: false,
  error: null,
  activeConflictImportId: null,
  resumableBatch: null,
};

function messageFrom(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

export function useImportWizard(campaignId: string, api: ImportBatchApi) {
  const [state, setState] = useState<ImportWizardState>(initialState);
  const setImportResult = useCampaignCapabilitiesStore((s) => s.setImportResult);
  const setImportProgress = useCampaignCapabilitiesStore((s) => s.setImportProgress);
  const clearImportSignals = useCallback(() => {
    setImportResult(null);
    setImportProgress(null);
  }, [setImportResult, setImportProgress]);

  // Best-effort: fetch the batch behind an active-import id and return it
  // only if its status is one this client can actually resume into — used
  // by both checkStatus (wizard just opened) and uploadFile's 409 path
  // (tried to start a fresh upload while one is already active).
  const fetchResumableBatch = useCallback(
    async (importId: string): Promise<ImportBatch | null> => {
      try {
        const batch = await api.getImportBatch(campaignId, importId);
        return stepForBatchStatus(batch.status) ? batch : null;
      } catch {
        return null;
      }
    },
    [campaignId, api],
  );

  // Fires when the wizard opens, before any file is chosen: checks whether a
  // batch is already active for this campaign (so the "already in progress"
  // card can show immediately instead of only after a wasted file-pick-and-
  // upload round trip) and fetches the expected columns for the upload
  // step's chips / template download. Best-effort — a failure here just
  // falls through to the normal empty upload step; the real active-import
  // guard still applies server-side on the actual upload attempt.
  const checkStatus = useCallback(async () => {
    setState((s) => ({ ...s, checkingActive: true, error: null }));
    try {
      const status = await api.getImportStatus(campaignId);
      const resumableBatch = status.activeImportId ? await fetchResumableBatch(status.activeImportId) : null;
      setState((s) => ({
        ...s,
        checkingActive: false,
        expectedFields: status.expectedFields,
        activeConflictImportId: status.activeImportId,
        resumableBatch,
      }));
    } catch {
      setState((s) => ({ ...s, checkingActive: false }));
    }
  }, [campaignId, api, fetchResumableBatch]);

  const uploadFile = useCallback(
    async (file: File) => {
      setState((s) => ({ ...s, busy: true, error: null, activeConflictImportId: null }));
      try {
        const result = await api.previewImport(campaignId, file);
        setState((s) => ({
          ...s,
          busy: false,
          step: 'mapping',
          importId: result.importId,
          fileName: file.name,
          sheetColumns: result.sheetColumns,
          expectedFields: result.expectedFields,
          rowCount: result.rowCount,
        }));
      } catch (err) {
        const details = conflictDetails<{ active_import_id: string }>(err);
        const conflictId = details?.active_import_id ?? null;
        const resumableBatch = conflictId ? await fetchResumableBatch(conflictId) : null;
        setState((s) => ({
          ...s,
          busy: false,
          error: messageFrom(err, "Couldn't read this sheet"),
          activeConflictImportId: conflictId,
          resumableBatch,
        }));
        throw err;
      }
    },
    [campaignId, api, fetchResumableBatch],
  );

  // Discards the batch reported by uploadFile's CONFLICT (a stale/orphaned
  // batch from a closed tab or crashed session — see activeConflictImportId)
  // so the admin can start a fresh upload without needing a Mongo/curl
  // escape hatch. Unlike closeAndDiscard (a genuinely fire-and-forget
  // best-effort call from the dialog's close button, with local state
  // already reset either way), this is triggered by an explicit "Discard
  // and try again" button the admin is watching for a result: if the
  // DELETE call actually fails, silently clearing activeConflictImportId
  // here would tell the admin it worked when it didn't, leaving them stuck
  // in a "pick a file, get the same 409 again" loop with no idea why (see
  // discardActiveConflict's PR review finding — deleting silently and
  // resetting the same fields on both success and failure hid a real
  // failure as if it were a success). Report the failure and leave
  // activeConflictImportId/resumableBatch in place — the admin can retry
  // the same button, and checkStatus/uploadFile's own 409 handling would
  // reconstruct the same state anyway on the next attempt.
  const discardActiveConflict = useCallback(async () => {
    const conflictId = state.activeConflictImportId;
    if (!conflictId) return;
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      await api.discardImport(campaignId, conflictId);
    } catch (err) {
      setState((s) => ({ ...s, busy: false, error: messageFrom(err, 'Could not discard the in-progress import — try again') }));
      throw err;
    }
    setState((s) => ({ ...s, busy: false, error: null, activeConflictImportId: null, resumableBatch: null }));
  }, [campaignId, api, state.activeConflictImportId]);

  // Rehydrates the wizard's local step machine from the already-fetched
  // `resumableBatch` — no extra round-trip, `checkStatus`/`uploadFile`
  // fetched it up front specifically so this can be synchronous. `mapping`
  // resume has no sample values or suggested mappings (only available from
  // the original /preview response, not the batch GET) — an accepted
  // degradation for the rare "refreshed between upload and mapping submit"
  // case, still lets the admin map columns manually rather than losing the
  // upload entirely.
  const resumeActiveImport = useCallback(() => {
    const batch = state.resumableBatch;
    if (!batch) return;
    const step = stepForBatchStatus(batch.status);
    if (!step) return;
    setState((s) => ({
      ...s,
      step,
      importId: batch.id,
      fileName: batch.fileName,
      rows: batch.rows,
      expectedFields: batch.expectedFields,
      sheetColumns: batch.headers.map((header) => ({
        header,
        sampleValue: '',
        suggestedField: batch.mapping[header] ?? '',
      })),
      rowCount: batch.rows.length,
      activeConflictImportId: null,
      resumableBatch: null,
      error: null,
    }));
  }, [state.resumableBatch]);

  // A sheet with no duplicates and no validation errors has nothing for the
  // admin to decide on Sheet Review — landing there would just show an
  // empty "nothing left to decide" screen they'd immediately click past.
  // Skip straight to Roster Review instead (and, by the same rule, straight
  // past THAT too if there are no roster conflicts either) — mirrors
  // continueToRosterReview's own skip below, so the outcome is identical
  // whether Roster Review is reached by auto-skip or by an explicit click.
  const submitMapping = useCallback(
    async (mapping: Record<string, string>) => {
      if (!state.importId) return;
      const importId = state.importId;
      setState((s) => ({ ...s, busy: true, error: null }));
      try {
        const batch = await api.submitMapping(campaignId, importId, mapping);
        if (hadSheetIssues(batch.rows)) {
          setState((s) => ({ ...s, busy: false, step: 'resolve-sheet-issues', rows: batch.rows }));
          return;
        }
        const advanced = await api.advanceToRosterReview(campaignId, importId);
        const step = conflictRows(advanced.rows).length === 0 ? 'confirm' : 'resolve-roster-conflicts';
        setState((s) => ({ ...s, busy: false, step, rows: advanced.rows }));
      } catch (err) {
        setState((s) => ({ ...s, busy: false, error: messageFrom(err, 'Could not save column mapping') }));
        throw err;
      }
    },
    [campaignId, api, state.importId],
  );

  const resolveSheetIssue = useCallback(
    async (rowNumber: number, action: 'keep' | 'drop' | 'fixed', values?: Record<string, string>) => {
      if (!state.importId) return;
      setState((s) => ({ ...s, busy: true, error: null }));
      try {
        const batch = await api.submitSheetResolution(campaignId, state.importId, { rowNumber, action, values });
        setState((s) => ({ ...s, busy: false, rows: batch.rows }));
      } catch (err) {
        setState((s) => ({ ...s, busy: false, error: messageFrom(err, 'Could not update this row') }));
        throw err;
      }
    },
    [campaignId, api, state.importId],
  );

  // No roster conflicts means nothing to decide here either — every row is
  // either brand new or already resolved by the server default (spec §3.2),
  // so skip straight to Confirm's "here's everything that will be written"
  // summary instead of a Roster Review screen with no actual choices on it.
  const continueToRosterReview = useCallback(async () => {
    if (!state.importId) return;
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const batch = await api.advanceToRosterReview(campaignId, state.importId);
      const step = conflictRows(batch.rows).length === 0 ? 'confirm' : 'resolve-roster-conflicts';
      setState((s) => ({ ...s, busy: false, step, rows: batch.rows }));
    } catch (err) {
      setState((s) => ({ ...s, busy: false, error: messageFrom(err, 'Could not move to roster review') }));
      throw err;
    }
  }, [campaignId, api, state.importId]);

  const resolveRosterConflict = useCallback(
    async (rowNumber: number, action: 'keep_existing' | 'overwrite' | 'skip', keepFields?: string[]) => {
      if (!state.importId) return;
      setState((s) => ({ ...s, busy: true, error: null }));
      try {
        const batch = await api.submitRosterResolution(campaignId, state.importId, { rowNumber, action, keepFields });
        setState((s) => ({ ...s, busy: false, rows: batch.rows }));
      } catch (err) {
        setState((s) => ({ ...s, busy: false, error: messageFrom(err, 'Could not update this row') }));
        throw err;
      }
    },
    [campaignId, api, state.importId],
  );

  const continueToConfirm = useCallback(() => {
    setState((s) => ({ ...s, step: 'confirm' }));
  }, []);

  // Upload and Mapping both sit at the batch's `mapping` status server-side
  // (mapping isn't submitted until ImportMappingStep's own Continue), so
  // moving between them is a pure client-side transition either way — no
  // server call, same as continueToConfirm/backToRosterReview below.
  const continueToMapping = useCallback(() => {
    setState((s) => (s.step === 'upload' && s.importId ? { ...s, step: 'mapping' } : s));
  }, []);
  const backToUpload = useCallback(() => {
    setState((s) => (s.step === 'mapping' ? { ...s, step: 'upload' } : s));
  }, []);

  // Sheet Review has its own server-side status (sheet_review — see
  // api.stepBackImport), so stepping back out of Mapping has to revert that
  // status server-side first, or Mapping's own Continue would immediately
  // 400 against the stale roster_review/sheet_review status.
  const backToMapping = useCallback(async () => {
    if (!state.importId) return;
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const batch = await api.stepBackImport(campaignId, state.importId); // sheet_review -> mapping
      setState((s) => ({ ...s, busy: false, step: 'mapping', rows: batch.rows }));
    } catch (err) {
      setState((s) => ({ ...s, busy: false, error: messageFrom(err, 'Could not go back') }));
      throw err;
    }
  }, [campaignId, api, state.importId]);

  // Same server-side-status requirement as backToMapping, plus the auto-skip
  // rule from submitMapping applied in reverse — with one sharp edge: the
  // check must be `hadSheetIssues`, NOT `isSheetReviewComplete`. By the time
  // a batch has reached roster_review, AdvanceToRosterReview's own
  // server-side validation has already GUARANTEED isSheetReviewComplete is
  // true (that's its precondition) — using it here would always evaluate
  // true and skip straight past Sheet Review to Mapping regardless of
  // whether the admin actually had (and resolved) real duplicates/errors
  // there. hadSheetIssues asks the right question: did Sheet Review ever
  // have anything to show at all, not "is everything currently resolved".
  const backToSheetIssues = useCallback(async () => {
    if (!state.importId) return;
    const importId = state.importId;
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const batch = await api.stepBackImport(campaignId, importId); // roster_review -> sheet_review
      if (!hadSheetIssues(batch.rows)) {
        const mappingBatch = await api.stepBackImport(campaignId, importId); // sheet_review -> mapping
        setState((s) => ({ ...s, busy: false, step: 'mapping', rows: mappingBatch.rows }));
        return;
      }
      setState((s) => ({ ...s, busy: false, step: 'resolve-sheet-issues', rows: batch.rows }));
    } catch (err) {
      setState((s) => ({ ...s, busy: false, error: messageFrom(err, 'Could not go back') }));
      throw err;
    }
  }, [campaignId, api, state.importId]);

  // 'confirm' has no server-side status of its own (the batch is still just
  // roster_review until commit runs), so stepping back to Roster Review is a
  // pure client-side transition when there's actually something to show
  // there. When Roster Review was itself auto-skipped forward (no
  // conflicts — continueToRosterReview/submitMapping's own rule), stepping
  // back into it would be the same empty screen all over again, so delegate
  // to backToSheetIssues's server-aware skip-chain instead.
  const backToRosterReview = useCallback(async () => {
    if (state.step !== 'confirm') return;
    if (conflictRows(state.rows).length > 0) {
      setState((s) => ({ ...s, step: 'resolve-roster-conflicts' }));
      return;
    }
    await backToSheetIssues();
  }, [state.step, state.rows, backToSheetIssues]);

  const commit = useCallback(async () => {
    if (!state.importId) return;
    const importId = state.importId;
    setState((s) => ({ ...s, busy: true, error: null }));
    // Clear whatever the store is still holding from a PREVIOUS import in this
    // session (mirrors the old upload mutation's onMutate) — otherwise the
    // 'importing' step's markDone effect sees a stale importResult and jumps
    // straight to 'done' with the wrong summary before this commit's own SSE
    // events ever arrive.
    clearImportSignals();
    try {
      await api.commitImport(campaignId, importId);
      setState((s) => ({ ...s, busy: false, step: 'importing' }));
    } catch (err) {
      const drift = conflictDetails<{ deflected_rows: number[] }>(err);
      if (drift) {
        // The roster changed underneath this review and deflected every
        // remaining row — a genuine conflict, not a dead end. Re-fetch the
        // batch (the backend already persisted the drift-recheck result
        // before returning this 409) and route back to roster review with
        // the fresh state instead of leaving the admin stuck on a stale
        // confirm screen with no recovery but discarding the whole import.
        try {
          const batch = await api.getImportBatch(campaignId, importId);
          setState((s) => ({
            ...s,
            busy: false,
            step: 'resolve-roster-conflicts',
            rows: batch.rows,
            error: messageFrom(err, 'The roster changed while this import was under review — review the affected rows again.'),
          }));
        } catch {
          // Re-fetch itself failed too — we can't show the FRESH drifted
          // rows, but leaving the admin on the stale Confirm screen with a
          // generic "could not start the import" message is worse: they'd
          // just retry Commit and hit the same 409 forever with no idea a
          // roster-drift review is what's actually needed. Still route back
          // to Roster Review (on the rows already in state — stale, but not
          // wiped) so the recovery path stays reachable, with a message
          // that explains BOTH failures instead of hiding the drift.
          setState((s) => ({
            ...s,
            busy: false,
            step: 'resolve-roster-conflicts',
            error: "The roster changed while this import was under review, and we couldn't fetch the latest state — review your roster resolutions again before retrying.",
          }));
        }
        throw err;
      }
      setState((s) => ({ ...s, busy: false, error: messageFrom(err, 'Could not start the import') }));
      throw err;
    }
  }, [campaignId, api, state.importId, clearImportSignals]);

  const markDone = useCallback(() => {
    setState((s) => (s.step === 'importing' ? { ...s, step: 'done' } : s));
    // Best-effort fetch of which rows the commit's roster-drift re-check
    // deflected (spec §5 edge case 5) — fire-and-forget, never blocks or
    // errors the transition to 'done' above. Guarded on state.importId at
    // call time (React batches the setState above with this synchronously,
    // so state.importId here is still the value from the render that
    // triggered markDone).
    if (state.importId) {
      const importId = state.importId;
      // async IIFE (not a bare .then/.catch chain) so a synchronous throw
      // from api.getImportBatch is caught too, not just a rejected promise.
      void (async () => {
        try {
          const batch = await api.getImportBatch(campaignId, importId);
          setState((s) => ({ ...s, deflectedRowNumbers: batch.deflectedRows }));
        } catch {
          // Deflected-row reporting is a nice-to-have surfaced on the done
          // screen, not load-bearing — a failed fetch just leaves the list
          // empty, same as if nothing was deflected.
        }
      })();
    }
  }, [campaignId, api, state.importId]);

  // Also clears the shared import-progress/result store, not just local
  // wizard state — otherwise a result already shown inline on the wizard's
  // 'done' step (or a stale one from a prior import) lingers in the store and
  // re-triggers CreatorsTab's fallback ImportStatusModal the instant the
  // wizard closes, even though the admin already saw it inline.
  const reset = useCallback(() => {
    setState(initialState);
    clearImportSignals();
  }, [clearImportSignals]);

  const closeAndDiscard = useCallback(async () => {
    const { importId, step } = state;
    reset();
    if (!importId || step === 'importing' || step === 'done') return;
    try {
      await api.discardImport(campaignId, importId);
    } catch {
      // Best-effort — the TTL index (spec §3.1) reclaims an orphaned batch
      // either way, and the batch is already gone from local state.
    }
  }, [state, campaignId, api, reset]);

  // Lets the admin pick a DIFFERENT file after Back from Column Mapping,
  // instead of only being able to re-confirm the one already uploaded
  // (ImportUploadReviewStep's read-only recap). The current batch is still
  // active server-side (unsubmitted, status `mapping`) — one-active-batch-
  // per-campaign means it has to be discarded before a fresh preview upload
  // can succeed. Same reset+re-check pattern as the 'done' step's "Upload
  // another" action, just with a discard of the in-flight batch first.
  //
  // Unlike closeAndDiscard (fire-and-forget: the admin is leaving the wizard
  // either way, and the TTL index reclaims an orphaned batch regardless), a
  // failed discard here is NOT invisible — checkStatus below re-finds the
  // still-active old batch and repopulates activeConflictImportId, so
  // without surfacing the error the admin would just see "Change file" put
  // them right back where they started with no idea why. Report it instead.
  const changeFile = useCallback(async () => {
    const importId = state.importId;
    setState((s) => ({ ...s, busy: true, error: null }));
    let discardErr: unknown = null;
    if (importId) {
      try {
        await api.discardImport(campaignId, importId);
      } catch (err) {
        discardErr = err;
      }
    }
    reset();
    await checkStatus();
    if (discardErr) {
      setState((s) => ({
        ...s,
        error: messageFrom(discardErr, "Couldn't discard the previous file — it's still active. Resume it below, or try Change file again."),
      }));
    }
  }, [campaignId, api, state.importId, reset, checkStatus]);

  return {
    ...state,
    // The step `resumableBatch` would resume into, for display purposes
    // (e.g. "pick up at Review") — derived, not stored, since it's a pure
    // function of state.resumableBatch and duplicating the mapping in the UI
    // layer would risk drifting from `resumeActiveImport`'s own logic.
    resumableStep: state.resumableBatch ? stepForBatchStatus(state.resumableBatch.status) : null,
    checkStatus,
    uploadFile,
    discardActiveConflict,
    resumeActiveImport,
    continueToMapping,
    backToUpload,
    changeFile,
    submitMapping,
    backToMapping,
    resolveSheetIssue,
    continueToRosterReview,
    backToSheetIssues,
    resolveRosterConflict,
    continueToConfirm,
    backToRosterReview,
    commit,
    markDone,
    reset,
    closeAndDiscard,
  };
}
